import { useState, useRef, useEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

export interface DescriptiveOption {
  value: string;
  label: string;
  description: string;
  icon?: ReactNode;
}

interface DescriptiveSelectProps {
  label?: string;
  optionalText?: string;
  value: string;
  onChange: (value: string) => void;
  options: DescriptiveOption[];
  placeholder?: string;
  error?: string;
  className?: string;
}

export default function DescriptiveSelect({
  label,
  optionalText,
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  error,
  className = '',
}: DescriptiveSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [maxHeight, setMaxHeight] = useState(320);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  const updateDropdownPosition = () => {
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownMaxHeight = 320;
      const margin = 16;
      const spaceBelow = viewportHeight - buttonRect.bottom - margin;
      const spaceAbove = buttonRect.top - margin;

      const shouldOpenUpward = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;
      setOpenUpward(shouldOpenUpward);

      const availableSpace = shouldOpenUpward ? spaceAbove : spaceBelow;
      setMaxHeight(Math.min(dropdownMaxHeight, Math.max(availableSpace, 150)));

      setDropdownPosition({
        top: shouldOpenUpward ? buttonRect.top : buttonRect.bottom + 8,
        left: buttonRect.left,
        width: buttonRect.width,
      });
    }
  };

  const handleToggleOpen = () => {
    if (!isOpen) {
      updateDropdownPosition();
    }
    setIsOpen(!isOpen);
  };

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideContainer = containerRef.current && !containerRef.current.contains(target);
      const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(target);
      if (isOutsideContainer && isOutsideDropdown) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update position on scroll/resize when open
  useEffect(() => {
    if (!isOpen) return;

    const handleScrollOrResize = () => {
      updateDropdownPosition();
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div className={`w-full ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          {label}
          {optionalText && (
            <span className="text-gray-400 dark:text-gray-500 font-normal ml-2">
              ({optionalText})
            </span>
          )}
        </label>
      )}
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={handleToggleOpen}
          className={`
            w-full px-4 py-3 rounded-xl text-left
            bg-white dark:bg-gray-800
            border-2 ${error ? 'border-red-500' : isOpen ? 'border-primary-500 dark:border-primary-400' : 'border-gray-200 dark:border-gray-700'}
            hover:border-primary-400 dark:hover:border-primary-600
            focus:outline-none focus:ring-4 ${error ? 'focus:ring-red-500/20' : 'focus:ring-primary-500/20'}
            transition-all duration-300
            shadow-sm hover:shadow-md
          `}
        >
          {selectedOption ? (
            <div className="pr-8 flex items-start gap-3">
              {selectedOption.icon && (
                <div className="flex-shrink-0 text-gray-500 dark:text-gray-400 mt-0.5">
                  {selectedOption.icon}
                </div>
              )}
              <div className="min-w-0">
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {selectedOption.label}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                  {selectedOption.description}
                </div>
              </div>
            </div>
          ) : (
            <span className="text-gray-400 dark:text-gray-500">{placeholder}</span>
          )}
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </button>
      </div>

      {/* Dropdown rendered via portal to escape overflow:hidden containers */}
      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, y: openUpward ? 10 : -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: openUpward ? 10 : -10 }}
              transition={{ duration: 0.15 }}
              style={{
                position: 'fixed',
                top: openUpward ? 'auto' : `${dropdownPosition.top}px`,
                bottom: openUpward ? `${window.innerHeight - dropdownPosition.top + 8}px` : 'auto',
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
                maxHeight: `${maxHeight}px`,
                zIndex: 9999,
              }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden overflow-y-auto"
            >
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className={`
                    w-full px-4 py-3 text-left transition-colors
                    ${
                      value === option.value
                        ? 'bg-primary-50 dark:bg-primary-900/30 border-l-4 border-primary-500'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 border-l-4 border-transparent'
                    }
                  `}
                >
                  <div className="flex items-start gap-3">
                    {option.icon && (
                      <div
                        className={`flex-shrink-0 mt-0.5 ${value === option.value ? 'text-primary-500 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'}`}
                      >
                        {option.icon}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div
                        className={`font-medium ${value === option.value ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-gray-100'}`}
                      >
                        {option.label}
                      </div>
                      <div
                        className={`text-sm ${value === option.value ? 'text-primary-600/80 dark:text-primary-400/80' : 'text-gray-500 dark:text-gray-400'}`}
                      >
                        {option.description}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
      {error && (
        <p className="mt-2 text-sm text-red-500 font-medium flex items-center gap-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
}
